import SockJS from "sockjs-client";
import Stomp, { Client } from "webstomp-client";
import { TYPE } from "vue-toastification";
import LeaderLine from "leader-line";
import {
  apiCall,
  apiCallParams,
  logResponse,
  handleResponse,
  getTenantId,
} from "@/core/api";
import { getParameter } from "@/core/util";
import { showAlert } from "@/core/popup";
import { sortedByIxAndIy } from "@/flow/domain/flow";
import REQUEST_TYPE from "@/core/api/request-types";
import i18n from "@/core/i18n/i18n";
import {
  collectFlowEntities,
  findFirstUnskippedStep,
  isStepSkipped,
  markStepLinkSkipped,
  normalizeFlowSkipState,
} from "@/flow/utils/skipUtils";

const { t } = i18n.global;

export async function getFlowList(): Promise<any> {
  const endpoint = "/flows";
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function exportFlow(exportDto: any): Promise<any> {
  const endpoint = "/flows/export";
  const response = await apiCall("POST", endpoint, exportDto);
  if (response.code === 200) {
    return response.message;
  }
  logResponse(response);
  return false;
}
export async function getSystemVariables(): Promise<any> {
  const endpoint = "/system-variables/keys";
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  handleResponse(response);
  return undefined;
}

export async function getExpressionDetails(): Promise<any> {
  const endpoint = "/expressions/details";
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  handleResponse(response);
  return undefined;
}
export async function getTenantDefault(tenantId: string): Promise<any> {
  const endpoint = `/executions/default/tenant/${tenantId}`;
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}
export async function getOrganizationsExecutions(
  organizationId: string
): Promise<any> {
  const endpoint = `/executions/organization/${organizationId}`;
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}
export async function getOrganizationDefault(
  organizationId: string
): Promise<any> {
  const endpoint = `/executions/default/${organizationId}`;
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export interface FlowScheduleFieldCapability {
  name: string;
  label?: string;
  type: string;
  required: boolean;
  options?: Array<Record<string, unknown>>;
  allowedValues?: unknown[];
  minimum?: number;
  maximum?: number;
}

export interface FlowScheduleModeCapability {
  value: string;
  label: string;
  mode: string;
  recurring: boolean;
  fields: FlowScheduleFieldCapability[];
}

export interface FlowScheduleCapabilities {
  contractVersion: number;
  serverTimeUtc: string;
  modes: FlowScheduleModeCapability[];
}

export interface FlowScheduleConfiguration {
  scheduleType: string;
  schedule: Record<string, unknown> | null;
  timeZone: string | null;
}

/** Java-owned schedule modes, fields, choices, and server clock. */
export async function getFlowScheduleCapabilities(): Promise<FlowScheduleCapabilities> {
  const response = await apiCall("GET", "/flows/schedule-capabilities");
  const result = Array.isArray(response.result)
    ? response.result[0]
    : response.result;
  if (response.code === 200 && result && Array.isArray(result.modes)) {
    const modes = result.modes.filter(
      (option: any) =>
        option &&
        typeof option.value === "string" &&
        typeof option.label === "string" &&
        typeof option.mode === "string" &&
        typeof option.recurring === "boolean" &&
        Array.isArray(option.fields)
    );
    return {
      contractVersion: Number(result.contractVersion),
      serverTimeUtc: String(result.serverTimeUtc || ""),
      modes,
    };
  }
  logResponse(response);
  throw new Error("The Java schedule capability contract is unavailable.");
}

/** Compatibility accessor for callers that only need the mode list. */
export async function getFlowScheduleOptions(): Promise<
  FlowScheduleModeCapability[]
> {
  return (await getFlowScheduleCapabilities()).modes;
}

/** Validate user-entered values and let Java build the canonical persisted schedule. */
export async function normalizeFlowSchedule(
  values: Record<string, unknown>
): Promise<FlowScheduleConfiguration> {
  const response = await apiCall("POST", "/flows/schedule/normalize", values);
  const result = Array.isArray(response.result)
    ? response.result[0]
    : response.result;
  // Deliberately keyed off `scheduleType` alone. The gateway serialises with
  // `spring.jackson.default-property-inclusion: non_null`, so an unscheduled result — which Java
  // returns as (NONE, null, null) — arrives as `{"scheduleType":"NONE"}` with `schedule` and
  // `timeZone` absent rather than null. Requiring a `schedule` key rejected that success.
  if (
    response.code === 200 &&
    result &&
    typeof result.scheduleType === "string"
  ) {
    return {
      scheduleType: result.scheduleType,
      schedule: result.schedule ?? null,
      timeZone: result.timeZone ?? null,
    };
  }
  throw new Error(response.message || "Java rejected the schedule values.");
}

export async function getConfigurationFramework(flowId: string): Promise<any> {
  const endpoint = `/configs/flow.execution/FLOW/${flowId}`;
  try {
    const response = await apiCall("GET", endpoint);
    if (response.code === 200) {
      return response.result;
    }
    logResponse(response);
    return undefined;
  } catch (error) {
    console.error("API Error:", error);
    showAlert(TYPE.WARNING, t("Flow.FetchError"));
    return undefined;
  }
}
export async function getExecutionProperties(): Promise<any> {
  const endpoint = "/properties/camel-gateway/docker/latest/flow.execution";
  try {
    const response = await apiCall("GET", endpoint);
    if (
      response.type === "SUCCESS" &&
      response.code === 200 &&
      Array.isArray(response.result)
    ) {
      const resultItem = response.result[0];
      const parsedValue = JSON.parse(resultItem.value);

      return {
        deleted: resultItem.deleted,
        key: resultItem.key,
        defaultValue: parsedValue.defaultValue || resultItem.value,
        description: resultItem.description,
        enumerationValues: parsedValue.enumerationValues || [],
      };
    }
    logResponse(response);
    return undefined;
  } catch (error) {
    console.error("API Error:", error);
    showAlert(TYPE.WARNING, t("Flow.FetchError"));
    return undefined;
  }
}

export async function updateConfigurationFramework(
  data: any,
  owner: string
): Promise<any> {
  const endpoint = "/configs";
  const requestBody = {
    key: data.label,
    level: "FLOW",
    value: data.inputValue,
    type: data.type,
    children: data.children || [],
    ownerId: owner,
    description: data.description,
    enumerationValues: data.enumerationValues || [],
  };

  try {
    const response = await apiCall("PUT", endpoint, requestBody);
    if (response.code !== 200) {
      logResponse(response);
    }
  } catch (error) {
    console.error("API Error:", error);
  }
}

export async function searchExpressionByName(name: string): Promise<any> {
  const endpoint = `/expressions/search/${name}`;
  const response = await apiCallParams("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  handleResponse(response);
  return undefined;
}

export async function verifyConnectors(
  flowId: string,
  organizationId: string
): Promise<any> {
  const endpoint = "/flows/verify";
  const response = await apiCallParams("GET", endpoint, {
    flowId,
    organizationId,
  });
  if (response.code === 200) {
    // The gateway decides connection-optional from the connector spec, independent of which
    // tenant has a connector row (OT-5175).
    return {
      verified: true,
      connectionOptionalSpecIds: (response.result || [])
        .filter(
          (connector) =>
            connector.connectionOptional && connector.connectorSpecId
        )
        .map((connector) => connector.connectorSpecId),
    };
  }
  if (response.code === 417) {
    return {
      verified: false,
      list: response.result,
    };
  }
  logResponse(response);
  return false;
}

export async function getFilteredFlowList(params: any): Promise<any> {
  const endpoint = "/flows/listing";
  const response = await apiCallParams("GET", endpoint, params);
  if (response.code === 200) {
    return response.page;
  }
  logResponse(response);
  return undefined;
}

export async function getActiveFlowList(): Promise<any> {
  const endpoint = "/flows/active";
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function getFlow(flowId: string, version: string): Promise<any> {
  const endpoint = `/flows/${flowId}/${version}`;
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    const flow = response.result[0];
    return flow?.newUi ? flow : sortedByIxAndIy(flow);
  }
  logResponse(response);
  return undefined;
}

export async function getRevision(
  connectorSpecId: number,
  connectionId?: string
): Promise<any> {
  let endpoint = `/registry/revisionNumbers/${connectorSpecId}`;
  if (connectionId != null) {
    endpoint += `/dynamic/${connectionId}`;
  }
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result
      .map((r) => Number(r))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
  }
  logResponse(response);
  return undefined;
}

export async function startFlow(flowId: string, version: string): Promise<any> {
  const endpoint = `/workflows/${flowId}/${version}/start`;
  const response = await apiCall("POST", endpoint);
  if (response.code === 200) {
    return response.result[0];
  }
  logResponse(response);
  return undefined;
}

export async function stopFlow(activityId: string): Promise<boolean> {
  const endpoint = `/workflows/activity/${activityId}/stop`;
  const response = await apiCall("POST", endpoint);
  if (response.code === 200) {
    return true;
  }
  logResponse(response);
  return false;
}

export async function restartFlow(activityId: string): Promise<boolean> {
  const endpoint = `/workflows/activity/${activityId}/start`;
  const response = await apiCall("POST", endpoint);
  if (response.code === 200) {
    return true;
  }
  logResponse(response);
  return false;
}

export async function deleteFlow(
  flowId: string,
  version: string,
  name: any
): Promise<boolean> {
  const endpoint = `/flows/${flowId}/${version}`;
  const response = await apiCall("DELETE", endpoint);
  if (response.code === 200) {
    showAlert(TYPE.SUCCESS, t("Flow.FlowDeletedWithName", { name }));
    return true;
  }
  if (response.code !== 200) {
    handleResponse(response);
  }
  logResponse(response);
  return false;
}

export async function inLineEditFlow(flowId: string, flow: any): Promise<any> {
  const endpoint = `/flows/edit/${flowId}`;
  const response = await apiCall("POST", endpoint, flow);
  if (response.code === 200) {
    if (response.result?.code === "1011") {
      showAlert(TYPE.WARNING, "Flow with the same name exists.");
      return undefined;
    }
    return true;
  }
  logResponse(response);
  return undefined;
}

export async function getConnections(): Promise<any> {
  const endpoint = "/connections/organization";
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

/**
 * Single source of truth for "is this a dynamic-spec connector?" on the frontend.
 * Relies on the explicit `dynamic` flag carried by the connector (set by the backend),
 * instead of inferring it from the connector spec id naming convention.
 */
export function isDynamicConnector(connector?: { dynamic?: boolean }): boolean {
  return connector?.dynamic === true;
}

export async function getAllActions(
  connectorSpecId: string,
  revision?: string,
  connectionId?: string
): Promise<any> {
  let endpoint = `/connectorui/${connectorSpecId}`;
  if (connectionId != null) {
    endpoint += `/dynamic/${connectionId}`;
  } else if (revision != null) {
    endpoint += `/${revision}`;
  }
  const response = await apiCall(
    "GET",
    endpoint,
    undefined,
    REQUEST_TYPE.CONNECTOR
  );
  if (response.code === 200) {
    return response.result.actions;
  }
  logResponse(response);
  return undefined;
}

export async function getConsumerActions(
  connectorSpecId: string,
  revision?: string,
  connectionId?: string
): Promise<any> {
  const actions = await getAllActions(connectorSpecId, revision, connectionId);
  return actions?.filter(
    (action) => action.actionSpec.actionType === "CONSUMER"
  );
}

export async function getProducerActions(
  connectorSpecId: string
): Promise<any> {
  const actions = await getAllActions(connectorSpecId);
  return actions?.filter(
    (action) => action.actionSpec.actionType === "PRODUCER"
  );
}

export async function addFlow(flow: Record<string, unknown>): Promise<any> {
  const endpoint = "/flows/add";

  let payload: Record<string, unknown> = { ...flow };

  try {
    const tenantId = (await getTenantId()) as string;
    const enabled = await isNewUiEnabledForTenant(tenantId);
    if (enabled) {
      payload = {
        ...flow,
        newUi: true,
      };
    }
  } catch (error) {
    console.error("isNewUiEnabledForTenant check failed:", error);
  }

  const response = await apiCall("POST", endpoint, payload);

  if (response.code === 200) {
    if (response.result?.code === "1011") {
      showAlert(TYPE.WARNING, "Flow with the same name exists.");
      return undefined;
    }
    return response.result[0];
  }
  logResponse(response);
  return undefined;
}

export async function editFlow(flow: Record<string, any>): Promise<any> {
  const endpoint = "/flows";
  const response = await apiCall("PUT", endpoint, flow);
  if (response.code === 200) {
    if (response.result?.code === "400") {
      showAlert(TYPE.WARNING, response.message);
      return undefined;
    }
    if (response.result?.code === "1012") {
      showAlert(TYPE.WARNING, response.message);
    }
    if (response.result?.code === "1016") {
      showAlert(TYPE.WARNING, response.message);
    }
    return response.result[0];
  }
  logResponse(response);
  return undefined;
}

export async function convertFlowToNewUi(
  flowId: string,
  version: string
): Promise<any> {
  const endpoint = `/flows/convert/${flowId}/${version}`;
  const response = await apiCall("PUT", endpoint);
  if (response.code === 200) {
    return response.result?.[0] || response.result;
  }
  logResponse(response);
  handleResponse(response);
  return undefined;
}

export async function simulateStep(
  stepId: string,
  flowId: string,
  flowVersion: string,
  bypassConditions = false
) {
  const endpoint = `/workflows/${stepId}/${flowId}/${flowVersion}/simulate${
    bypassConditions ? "?bypassConditions=true" : ""
  }`;
  const response = await apiCall("POST", endpoint);
  if (response.code === 200) {
    return response.result[0];
  }
  logResponse(response);
  return undefined;
}

export async function getStep(stepId: string): Promise<any> {
  const endpoint = `/steps/${stepId}`;
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result[0];
  }
  logResponse(response);
  return undefined;
}

export async function getActiveFlowCount(): Promise<any> {
  const endpoint = "/flows/organization";
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result[0];
  }
  logResponse(response);
  return undefined;
}

export async function getRunningFlowsCount(): Promise<any> {
  const endpoint = "/flows/running";
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result[0];
  }
  logResponse(response);
  return undefined;
}

export async function connectWebsocket(): Promise<Client> {
  const websocketUrl = await getParameter("WEBSOCKET_URL");
  const socket = new SockJS(websocketUrl);
  return Stomp.over(socket);
}

export function isRunning(flowStatusType: string): boolean {
  switch (flowStatusType) {
    case "STARTED":
    case "STARTING":
    case "TRIGGERED":
    case "INPROGRESS":
    case "RETRYING":
      return true;
    case "PUBLISHED":
    case "STOPPED":
    case "ABORTED":
    case "FAILED":
    case "COMPLETED":
      return false;
    default:
      return false;
  }
}

export function isFailed(flowStatusType: string): boolean {
  return flowStatusType === "FAILED";
}

export function isSkipped(flowStatusType: string): boolean {
  return flowStatusType === "SKIPPED";
}

export async function getElasticLogs(
  flowId: string,
  flowVersion: number
): Promise<any> {
  const endpoint = `/flow_logs/latest/${flowId}/${flowVersion}`;
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  return undefined;
}

export async function getLatestLogs(flowId: string): Promise<any> {
  const endpoint = `/flow_logs/latest/${flowId}`;
  const response = await apiCall("GET", endpoint, flowId);
  if (response.code === 200) {
    return response.result;
  }
  return undefined;
}

export async function getLatestStartedLogs(flowId: string): Promise<any> {
  const endpoint = `/flow_logs/latest-running/${flowId}`;
  const response = await apiCall("GET", endpoint, flowId);
  if (response.code === 200) {
    return response.result;
  }
  return undefined;
}

export async function getExchangeLog(activityId: string): Promise<any> {
  const endpoint = `/exchange_logs/activity/${activityId}`;
  const response = await apiCall("GET", endpoint, activityId);
  if (response.code === 200) {
    return response.result;
  }
  return undefined;
}

export async function exportFlowJson(flowId: string): Promise<any> {
  const endpoint = `/flows/export/${flowId}`;
  const response = await apiCall("GET", endpoint, flowId);
  if (response.code === 200) {
    const text = JSON.stringify(response.result);
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "file.json");
    document.body.appendChild(link);
    link.click();
  }
  logResponse(response);
  return undefined;
}

export async function checkFlowNameExists(
  organizationId: string,
  flowName: string
): Promise<any> {
  const endpoint = "/flows/name";
  const response = await apiCallParams("GET", endpoint, {
    organizationId,
    flowName,
  });
  if (response.code === 200) {
    return "NOT_EXIST";
  }
  if (response.code === 417) {
    return "EXIST";
  }
  logResponse(response);
  return "ERROR";
}

export async function cloneFlow(
  flowId: string,
  flowName: string
): Promise<any> {
  const endpoint = "/flows/clone";
  const date = new Date();
  const response = await apiCallParams("POST", endpoint, {
    flowId,
    flowName: `${flowName}_CLONE_${date.toDateString()} ${date.toLocaleTimeString()}`,
  });
  if (response.code === 200) {
    return true;
  }
  logResponse(response);
  return false;
}

export async function getFlowVersions(flowId: string): Promise<any> {
  const endpoint = `/flows/versions/${flowId}`;
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function revertFlow(flowId: string, version: any): Promise<any> {
  const endpoint = "/flows/revert";

  const response = await apiCallParams("POST", endpoint, {
    flowId,
    version,
  });

  if (response.code === 200) {
    return response.result[0];
  }

  logResponse(response);
  return undefined;
}

export function convertDate(dateStr?: string): string {
  if (dateStr) {
    const milliseconds = Date.parse(dateStr);
    const date = new Date(milliseconds);
    return date.toLocaleString();
  }
  return "";
}
export async function getModifiedUser(modifiedById: string): Promise<any> {
  const endpoint = `/users/get/${modifiedById}`;
  const response = await apiCall("GET", endpoint, modifiedById);
  if (response.code === 200) {
    const name = response.result[0]?.name || "";
    const lastname = response.result[0]?.lastname || "";
    return `${name} ${lastname}`;
  }
  return "";
}

export async function getFlowActiveValue(
  flowId: string,
  version: string
): Promise<any> {
  const endpoint = `/flows/active/${flowId}/${version}`;
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function getCreatedAtByFlowIdAndVersion(
  flowId: string,
  version: string
): Promise<any> {
  const endpoint = `/flows/createdAt/${flowId}/${version}`;
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function getCreatedByByFlowIdAndVersion(
  flowId: string,
  version: string
): Promise<any> {
  const endpoint = `/flows/createdBy/${flowId}/${version}`;
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function validateCronExpression(
  cronExpr: string
): Promise<boolean> {
  const endpoint = "/jobs/validate-cron";
  const response = await apiCallParams("GET", endpoint, { cronExpr });
  if (response.code === 200) {
    return response.message === "true";
  }
  logResponse(response);
  return undefined;
}

export function statusColor(flowStatusType: any): string {
  switch (flowStatusType) {
    case "STARTED":
    case "STARTING":
    case "TRIGGERED":
    case "PROGRESS":
    case "RETRYING":
      return "success";
    case "ABORTED":
    case "FAILED":
      return "danger";
    case "STOPPED":
    case "WARNING":
      return "warning";
    case "COMPLETED":
    case "PUBLISHED":
      return "";
    default:
      return "";
  }
}

export interface Flow {
  name?: string;
  description?: string;
  port?: number;
  pid?: number;
  version?: number;
  latest?: boolean;
  flowStatusType?: string;
  currentActivityStatus?: string;
  scheduleType?: string;
  schedule?: {
    cronExpr: string;
    scheduleTime: string;
    nextFireTime: string;
    timeZone: string;
    settings: Record<string, unknown>;
  };
  activities?: [];
  trigger?: any;
  steps?: [];
  flowLines?: any[];
  webhookPayloads?: [];
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
  timeZone?: string;
  createdBy?: any;
  modifiedBy?: any;
  createdOrgId?: any;
  continueOnFailure?: boolean;
  skillGenerationEnabled?: boolean;
  routerSelected?: boolean;
  userDefinedVariables?: Record<string, string>;
  newUi?: boolean;
}

export interface LoopItem {
  startIdx: number;
  endIdx: number;
}

export interface Route {
  prevStepIndex?: number;
  parentRouteIndex?: number;
  conditionalRoutes: Array<ConditionalRoutes>;
}

export interface ConditionalRoutes {
  condition?: any;
  steps?: any[];
}

export function createLine(
  start: any,
  end: any,
  lineParams: any,
  containerName: string,
  wrapped = false
): LeaderLine {
  LeaderLine.positionByWindowResize = false;
  // if wrapped, skip elementInViewport check
  if (
    start &&
    end &&
    (wrapped ||
      (!wrapped &&
        elementInViewport(start, containerName) &&
        elementInViewport(end, containerName)))
  ) {
    return new LeaderLine(
      LeaderLine.pointAnchor(start, {
        x: end.clientLeft + lineParams.startLeft,
        y: end.clientTop + lineParams.startTop,
      }),
      LeaderLine.pointAnchor(end, {
        x: end.clientLeft + lineParams.endLeft,
        y: end.clientTop + lineParams.endTop,
      }),
      {
        color: lineParams.color,
        startPlugColor: lineParams.startPlugColor,
        endPlugColor: lineParams.endPlugColor,
        size: lineParams.size,
        endPlug: "behind",
        startSocket: lineParams.startSocket,
        endSocket: lineParams.endSocket,
        gradient: lineParams.gradient,
        dash: lineParams.dash,
      }
    );
  }
  // workaround for dash square issue on condition dash svg line
  const wrapper = document.getElementById("flow-line-wrapper");
  const svgElement = wrapper.querySelectorAll("use");
  svgElement.forEach((el) => {
    el.style.strokeDashoffset = "2px";
  });
  return null;
}

const intersection = (r1, r2) => {
  const xOverlap = Math.max(
    0,
    Math.min(r1.x + r1.w, r2.x + r2.w) - Math.max(r1.x, r2.x)
  );
  const yOverlap = Math.max(
    0,
    Math.min(r1.y + r1.h, r2.y + r2.h) - Math.max(r1.y, r2.y)
  );

  return xOverlap * yOverlap;
};

function elementInViewport(element, containerName) {
  const bounding = element.getBoundingClientRect();
  const container = document.getElementById(containerName);
  const dimension = {
    x: bounding.x,
    y: bounding.y,
    w: bounding.width,
    h: bounding.height,
  };
  const viewport = {
    x: container.offsetLeft,
    y: container.offsetTop,
    w: container.clientWidth || window.innerWidth,
    h: container.clientHeight || window.innerHeight,
  };
  const divSize = dimension.w * dimension.h;
  const overlap = intersection(dimension, viewport);
  return overlap / divSize > 0.15;
}

export function appendLeaderLines(element: HTMLElement): void {
  const lineElements = document.querySelectorAll(".leader-line");
  lineElements.forEach((lineElement) => {
    if (
      !lineElement
        .querySelector("use")
        .getAttribute("style")
        .includes("stroke-width: 4px")
    ) {
      element.appendChild(lineElement);
    }
  });
}

export function removeLeaderLines(element: HTMLElement, callback): void {
  if (element) {
    element.innerHTML = "";
    if (callback) {
      callback();
    }
  }
}

export function setWrapperPosition(element: HTMLElement): void {
  element.style.transform = "none";
  const rectWrapper = element.getBoundingClientRect();
  // Move to the origin of coordinates as the document
  element.style.transform = `translate(${
    // eslint-disable-next-line no-restricted-globals
    -(rectWrapper.left + scrollX)
  }px, ${
    // eslint-disable-next-line no-restricted-globals
    -(rectWrapper.top + scrollY)
  }px)`;
}

function checkNextStepTypeIsRouter(list, index) {
  return !!list[index]?.type;
}

function checkNextStepTypeIsStep(list, index) {
  return !list[index]?.type;
}

export async function getScheduledActivities(): Promise<any> {
  const resource = "/flows/jobs";
  const response = await apiCallParams("GET", resource);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function isNewUiEnabledForTenant(
  tenantId: string
): Promise<boolean> {
  return getTenantConfig("ui.enable-new-ui", tenantId);
}

export async function isAIFlowReportingEnabledForTenant(
  tenantId: string
): Promise<boolean> {
  return getTenantConfig("ai.flow.report.enabled", tenantId);
}

export async function isAIJoltEnabledForTenant(
  tenantId: string
): Promise<boolean> {
  return getTenantConfig("ai.jolt.enabled", tenantId);
}

export async function isAIJoltAutoValidateEnabled(): Promise<boolean> {
  const value = await getProperty("ai.jolt.auto-validate-after-generation");
  // Robust to string / boolean / number forms (mirrors getTenantConfig).
  return String(value).toLowerCase() === "true" || String(value) === "1";
}

export async function isAIJoltFastResolveEnabled(): Promise<boolean> {
  const value = await getProperty("ai.jolt.fast-resolve-enabled");
  return String(value).toLowerCase() === "true" || String(value) === "1";
}

export interface JoltResolveFieldsRequest {
  inputField?: string;
  outputField?: string;
  promptField?: string;
  cachedStepOutputs: Record<string, unknown>;
  userDefinedVariables?: string;
}

export interface JoltResolveFieldsResult {
  resolvedInput?: string;
  resolvedOutput?: string;
  resolvedPrompt?: string;
  fallbackRequired: boolean;
  reason?: string;
}

/**
 * Resolve the transformation step's input/output/prompt fields from cached upstream step outputs
 * without a full simulation. Returns { fallbackRequired: true } when the backend determines the
 * fields cannot be resolved faithfully offline (caller should then run the full simulate).
 */
export async function resolveJoltFields(
  request: JoltResolveFieldsRequest
): Promise<JoltResolveFieldsResult> {
  const response = await apiCall(
    "POST",
    "/jolt/transformations/resolve-fields",
    request
  );
  if (
    response.code === 200 &&
    response.result &&
    typeof response.result.fallbackRequired === "boolean"
  ) {
    return response.result as JoltResolveFieldsResult;
  }
  // Any non-200 / malformed response → safe fallback to full simulation.
  return { fallbackRequired: true, reason: "resolve request failed" };
}

export interface JoltRecommendPromptsRequest {
  inputField?: string;
  cachedStepOutputs: Record<string, unknown>;
  userDefinedVariables?: string;
}

/**
 * Best-effort prompt suggestions for the transformation Output/Prompt field, based on the input
 * resolved from cached step outputs. Always resolves to an array (empty on any failure) so the UI
 * simply shows nothing when suggestions aren't available.
 */
export async function recommendPrompts(
  request: JoltRecommendPromptsRequest
): Promise<string[]> {
  try {
    // Fetched fresh on every call (like the generate flow) so property changes take
    // effect without a page reload.
    const intentCheckModel = await getProperty("ai.jolt.intent-check-model");
    const response = await apiCall(
      "POST",
      "/jolt/transformations/recommend-prompts",
      { ...request, modelId: intentCheckModel || undefined }
    );
    if (
      response.code === 200 &&
      response.result &&
      Array.isArray(response.result.suggestions)
    ) {
      return response.result.suggestions.filter(
        (s: unknown): s is string =>
          typeof s === "string" && s.trim().length > 0
      );
    }
  } catch (err) {
    // ignore — suggestions are optional
    console.debug("[recommendPrompts] failed to fetch suggestions:", err);
  }
  return [];
}

export async function isAIWizardEnabledForTenant(
  tenantId: string
): Promise<boolean> {
  return getTenantConfig("ai.wizard.enabled", tenantId);
}

export async function isAISkillGeneratorEnabledForTenant(
  tenantId: string
): Promise<boolean> {
  return getTenantConfig("ai.skill.generator.enabled", tenantId);
}

export async function generateFlowSkill(
  flowJson: any,
  flowId: string,
  flowVersion: number,
  skillGenerationEnabled: boolean
): Promise<{ jobId: string; flowName: string }> {
  const endpoint = "/wizard/skills/generate";
  try {
    const response = await apiCall("POST", endpoint, {
      flow_json: flowJson,
      flow_id: flowId,
      flow_version: flowVersion,
      skill_generation_enabled: skillGenerationEnabled,
    });
    if (response.code === 202) {
      return {
        jobId: response.result?.job_id,
        flowName: response.result?.flow_name,
      };
    }
    throw new Error(
      response.result?.message ||
        response.result?.detail?.message ||
        response.message ||
        `Skill generation failed (${response.code})`
    );
  } catch (error) {
    console.error("API Error (generateFlowSkill):", error);
    throw error;
  }
}

export async function getFlowSkillStatus(
  flowId: string,
  flowVersion: number | string | null,
  jobId?: string
): Promise<{
  exists: boolean;
  flowVersion: number | null;
  skillCount: number;
  status: string;
  jobId: string | null;
}> {
  const encodedFlowId = encodeURIComponent(flowId);
  const queryParams = [`flow_id=${encodedFlowId}`];
  if (flowVersion != null) {
    queryParams.push(`flow_version=${encodeURIComponent(String(flowVersion))}`);
  }
  if (jobId) {
    queryParams.push(`job_id=${encodeURIComponent(jobId)}`);
  }
  const endpoint = `/wizard/skills/status?${queryParams.join("&")}`;
  try {
    const response = await apiCall("GET", endpoint);
    if (response.code === 200) {
      return {
        exists: response.result?.exists === true,
        flowVersion: response.result?.flow_version ?? null,
        skillCount: response.result?.skill_count ?? 0,
        status: response.result?.status ?? "not_found",
        jobId: response.result?.job_id ?? null,
      };
    }
    return {
      exists: false,
      flowVersion: null,
      skillCount: 0,
      status: "error",
      jobId: null,
    };
  } catch (error) {
    console.error("API Error (getFlowSkillStatus):", error);
    return {
      exists: false,
      flowVersion: null,
      skillCount: 0,
      status: "error",
      jobId: null,
    };
  }
}

const SKILL_GEN_JOB_KEY = "oneteg:skillGen:inProgress";
const SKILL_GEN_JOB_TTL_MS = 15 * 60 * 1000; // 15 minutes

function skillGenJobCacheKey(
  flowId: string,
  flowVersion: number | null
): string {
  return `${flowId}:${
    flowVersion != null && Number.isFinite(flowVersion)
      ? flowVersion
      : "unknown"
  }`;
}

function normalizeSkillGenJobFlowVersion(flowVersion: unknown): number | null {
  if (flowVersion == null) return null;
  const parsed = Number(flowVersion);
  return Number.isFinite(parsed) ? parsed : null;
}

function readSkillGenJobs(): Record<string, any> {
  const raw = localStorage.getItem(SKILL_GEN_JOB_KEY);
  if (!raw) return {};
  const parsed = JSON.parse(raw);
  if (parsed?.flowId && parsed?.jobId) {
    const flowVersion = normalizeSkillGenJobFlowVersion(parsed.flowVersion);
    return { [skillGenJobCacheKey(parsed.flowId, flowVersion)]: parsed };
  }
  if (!parsed || typeof parsed !== "object") return {};
  return Object.values(parsed).reduce<Record<string, any>>((jobs, job: any) => {
    if (!job?.flowId || !job?.jobId) return jobs;
    const flowVersion = normalizeSkillGenJobFlowVersion(job.flowVersion);
    jobs[skillGenJobCacheKey(job.flowId, flowVersion)] = job;
    return jobs;
  }, {});
}

export function persistSkillGenJob(
  flowId: string,
  jobId: string,
  flowVersion: number
): void {
  try {
    const jobs = readSkillGenJobs();
    jobs[skillGenJobCacheKey(flowId, flowVersion)] = {
      flowId,
      jobId,
      flowVersion,
      startedAt: Date.now(),
    };
    localStorage.setItem(SKILL_GEN_JOB_KEY, JSON.stringify(jobs));
  } catch {
    // Ignore storage failures; polling can still continue in memory.
  }
}

export function clearSkillGenJob(
  flowId?: string,
  flowVersion?: number | null
): void {
  try {
    if (!flowId) {
      localStorage.removeItem(SKILL_GEN_JOB_KEY);
      return;
    }
    const jobs = readSkillGenJobs();
    if (flowVersion !== undefined) {
      delete jobs[skillGenJobCacheKey(flowId, flowVersion)];
    } else {
      Object.keys(jobs).forEach((key) => {
        if (jobs[key]?.flowId === flowId || key.startsWith(`${flowId}:`)) {
          delete jobs[key];
        }
      });
    }
    if (Object.keys(jobs).length === 0) {
      localStorage.removeItem(SKILL_GEN_JOB_KEY);
    } else {
      localStorage.setItem(SKILL_GEN_JOB_KEY, JSON.stringify(jobs));
    }
  } catch {
    // Ignore storage failures; stale entries expire through the TTL.
  }
}

export function getPersistedSkillGenJob(
  flowId: string,
  flowVersion: number | null
): { jobId: string; flowVersion: number | null } | null {
  try {
    const job = readSkillGenJobs()[skillGenJobCacheKey(flowId, flowVersion)];
    if (!job) return null;
    const jobFlowVersion = normalizeSkillGenJobFlowVersion(job.flowVersion);
    if (
      job.flowId === flowId &&
      jobFlowVersion === flowVersion &&
      Date.now() - job.startedAt < SKILL_GEN_JOB_TTL_MS
    ) {
      return {
        jobId: job.jobId,
        flowVersion: jobFlowVersion,
      };
    }
    clearSkillGenJob(flowId, flowVersion);
    return null;
  } catch {
    return null;
  }
}

export async function getTenantConfig(
  configKey: string,
  tenantId: string
): Promise<boolean> {
  const endpoint = `/configs/${configKey}/TENANT/${tenantId}`;

  try {
    const response = await apiCall("GET", endpoint);

    if (response.code === 200) {
      const value = response.result[0]?.value;
      return value === true || value === "true" || value === 1 || value === "1";
    }

    logResponse(response);
    return false;
  } catch (error) {
    console.error(`API Error (getTenantConfig - ${configKey}):`, error);
    return false;
  }
}

export async function getProperty(key: string): Promise<string | undefined> {
  const configUrlPath = await getParameter("CONFIG_URL_PATH");
  const endpoint = `${configUrlPath}${key}`;
  try {
    const response = await apiCall("GET", endpoint);
    if (response.code === 200) {
      return response.result[0]?.value;
    }
    return undefined;
  } catch (error) {
    console.error(`API Error (getProperty - ${key}):`, error);
    return undefined;
  }
}

export function createStepLines(
  list: Array<any>,
  listIdx: string,
  element: HTMLElement,
  wrapped: boolean,
  callback
): void {
  const stepCount = document.getElementsByClassName("stepBoxCount").length;
  if (!element || element?.childElementCount === stepCount - 1) {
    return;
  }
  const horizontalLineParams = {
    startIdx: 0,
    endIdx: list.length,
    startLeft: 120,
    startTop: 80,
    endLeft: 0,
    endTop: 80,
    clientWidth: 210,
    gradient: true,
    color: undefined,
    startPlugColor: "#e5a1ac",
    endPlugColor: "#5022a6",
    startSocket: "right",
    endSocket: "left",
    size: 6,
    dash: undefined,
  };
  const verticalLineParams = {
    startIdx: 0,
    endIdx: list.length,
    startLeft: 60,
    startTop: 120,
    endLeft: 60,
    endTop: 0,
    clientWidth: 210,
    gradient: true,
    color: undefined,
    startPlugColor: "#e5a1ac",
    endPlugColor: "#5022a6",
    startSocket: "bottom",
    endSocket: "top",
    size: 8,
    dash: {
      len: 8,
      gap: 4,
    },
  };

  list.forEach((step) => {
    let endIndex = `${step.uniqueId
      .toString()
      .split("-")
      .slice(0, -1)
      .join("-")}-${
      parseInt(step.uniqueId?.toString().split("-").slice(-1)[0], 0) + 1
    }`;
    if (
      parseInt(step.uniqueId, 0).toString().length ===
      step.uniqueId.toString().length
    ) {
      endIndex = (parseInt(step.uniqueId, 0) + 1).toString();
    }
    const start =
      document.getElementById(`step-unique-${step.uniqueId}`) ||
      document.getElementById(`router-unique-${step.uniqueId}`);
    const end =
      document.getElementById(`step-unique-${endIndex}`) ||
      document.getElementById(`router-unique-${endIndex}`);
    if (start && end) {
      createLine(start, end, horizontalLineParams, "left-container", wrapped);
    }
    if (step.routes && step.routes.length > 0) {
      step.routes.forEach((route, routeIndex) => {
        const startRoute =
          routeIndex === 0
            ? document.getElementById(`router-unique-${step.uniqueId}`)
            : document.getElementById(`step-unique-${step.uniqueId}-0-0`);
        const endRoute = document.getElementById(
          `step-unique-${step.uniqueId}-${routeIndex}-0`
        );
        if (startRoute && endRoute) {
          createLine(
            startRoute,
            endRoute,
            verticalLineParams,
            "left-container",
            wrapped
          );
        }
        createStepLines(
          route.steps,
          `${listIdx}-${routeIndex.toString()}`,
          element,
          wrapped,
          null
        );
      });
    }
  });
  setWrapperPosition(element);
  appendLeaderLines(element);
  if (callback) {
    callback();
  }
}

export function createStepLinesChunked(
  list: Array<any>,
  listIdx: string,
  element: HTMLElement,
  wrapped: boolean,
  callback
): void {
  const stepCount = document.getElementsByClassName("stepBoxCount").length;
  if (!element || element?.childElementCount === stepCount - 1) {
    return;
  }
  const horizontalLineParams = {
    startIdx: 0,
    endIdx: list.length,
    startLeft: 120,
    startTop: 80,
    endLeft: 0,
    endTop: 80,
    clientWidth: 210,
    gradient: true,
    color: undefined,
    startPlugColor: "#e5a1ac",
    endPlugColor: "#5022a6",
    startSocket: "right",
    endSocket: "left",
    size: 6,
    dash: undefined,
  };
  const verticalLineParams = {
    startIdx: 0,
    endIdx: list.length,
    startLeft: 60,
    startTop: 120,
    endLeft: 60,
    endTop: 0,
    clientWidth: 210,
    gradient: true,
    color: undefined,
    startPlugColor: "#e5a1ac",
    endPlugColor: "#5022a6",
    startSocket: "bottom",
    endSocket: "top",
    size: 8,
    dash: {
      len: 8,
      gap: 4,
    },
  };

  const tasks: Array<{ start: any; end: any; params: any }> = [];

  function collectTasks(stepList: Array<any>, idx: string) {
    stepList.forEach((step) => {
      let endIndex = `${step.uniqueId
        .toString()
        .split("-")
        .slice(0, -1)
        .join("-")}-${
        parseInt(step.uniqueId?.toString().split("-").slice(-1)[0], 0) + 1
      }`;
      if (
        parseInt(step.uniqueId, 0).toString().length ===
        step.uniqueId.toString().length
      ) {
        endIndex = (parseInt(step.uniqueId, 0) + 1).toString();
      }
      const start =
        document.getElementById(`step-unique-${step.uniqueId}`) ||
        document.getElementById(`router-unique-${step.uniqueId}`);
      const end =
        document.getElementById(`step-unique-${endIndex}`) ||
        document.getElementById(`router-unique-${endIndex}`);
      if (start && end) {
        tasks.push({ start, end, params: horizontalLineParams });
      }
      if (step.routes && step.routes.length > 0) {
        step.routes.forEach((route, routeIndex) => {
          const startRoute =
            routeIndex === 0
              ? document.getElementById(`router-unique-${step.uniqueId}`)
              : document.getElementById(`step-unique-${step.uniqueId}-0-0`);
          const endRoute = document.getElementById(
            `step-unique-${step.uniqueId}-${routeIndex}-0`
          );
          if (startRoute && endRoute) {
            tasks.push({
              start: startRoute,
              end: endRoute,
              params: verticalLineParams,
            });
          }
          collectTasks(route.steps, `${idx}-${routeIndex.toString()}`);
        });
      }
    });
  }

  collectTasks(list, listIdx);

  for (let i = 0; i < tasks.length; i++) {
    createLine(
      tasks[i].start,
      tasks[i].end,
      tasks[i].params,
      "left-container",
      wrapped
    );
  }
  setWrapperPosition(element);
  appendLeaderLines(element);
  if (callback) {
    callback();
  }
}

export async function addSourceAndTarget(flow) {
  if (!flow || typeof flow !== "object") return flow;

  const flowLines = Array.isArray(flow.flowLines) ? flow.flowLines : [];
  normalizeFlowSkipState(flow.steps);
  const parseFlowLineData = (edge) => {
    const data = edge && edge.data;
    if (!data) return {};
    if (typeof data === "object") return data;
    if (typeof data !== "string") return {};

    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  };
  const isInactiveFlowLine = (edge) => {
    if (!edge) return true;
    const data = parseFlowLineData(edge);
    const hidden = edge.hidden || data.hidden;

    return hidden && !data.exitConnection;
  };
  const activeFlowLines = flowLines.filter((edge) => !isInactiveFlowLine(edge));

  const allSteps = [];
  const byId = new Map();
  const validIdList = [];
  const validIds = new Set();

  const pushValidId = (id) => {
    if (!id || validIds.has(id)) return;
    validIds.add(id);
    validIdList.push(id);
  };

  const collectSteps = (steps) => {
    if (!Array.isArray(steps)) return;
    steps.forEach((s) => {
      if (!s) return;
      allSteps.push(s);
      if (s.origId) {
        byId.set(s.origId, s);
        pushValidId(s.origId);
      }

      if (Array.isArray(s && s.routes)) {
        s.routes.forEach((r) => {
          pushValidId(r && r.origId);
          if (r && Array.isArray(r.steps)) collectSteps(r.steps);
        });
      }

      if (Array.isArray(s && s.steps)) collectSteps(s.steps);
    });
  };

  collectSteps(flow.steps);

  const entityById = new Map();
  collectFlowEntities(flow.steps).forEach((entity) => {
    if (entity?.origId) {
      entityById.set(String(entity.origId), entity);
    }
  });

  const isSkippedId = (idValue) => {
    const entity = entityById.get(String(idValue ?? ""));
    return Boolean(entity && isStepSkipped(entity));
  };

  const extractIds = (slot) => {
    const list = slot && slot.stepLinkData;
    if (!Array.isArray(list)) return [];
    return list.map((e) => e && e.id).filter((id) => Boolean(id));
  };

  // Preserve previous link ids so we can mark them as disconnected (connected:false)
  // instead of fully dropping them when an edge is removed from flowLines.
  const previousLinks = new Map();
  allSteps.forEach((s) => {
    if (!s || !s.origId) return;
    previousLinks.set(s.origId, {
      sourceIds: extractIds(s && s.source),
      targetIds: extractIds(s && s.target),
    });
  });

  const startTargets = new Set();

  const addLink = (slot, idValue) => {
    if (slot === null) return null;
    if (!idValue || !validIds.has(idValue)) return slot;

    const obj = slot || { stepLinkData: [] };
    const existing = obj.stepLinkData.find((e) => e && e.id === idValue);
    if (existing) {
      existing.connected = true;
      return obj;
    }

    obj.stepLinkData.push({ id: idValue, connected: true });
    return obj;
  };

  const addSkippedLink = (slot, idValue) => {
    if (slot === null) return null;
    if (!idValue || !validIds.has(idValue)) return slot;

    const obj = slot || { stepLinkData: [] };
    const existing = obj.stepLinkData.find((e) => e && e.id === idValue);
    if (existing) {
      markStepLinkSkipped(existing);
      return obj;
    }

    const link = { id: idValue, connected: false };
    markStepLinkSkipped(link);
    obj.stepLinkData.push(link);
    return obj;
  };

  const mergeDisconnected = (slot, preservedIds) => {
    if (slot === null) return null;

    const ids = Array.isArray(preservedIds) ? preservedIds : [];
    if (!ids.length && !slot) return slot;

    const obj = slot || { stepLinkData: [] };
    const currentIds = new Set(extractIds(obj));

    ids.forEach((idValue) => {
      if (!idValue || !validIds.has(idValue) || currentIds.has(idValue)) return;
      obj.stepLinkData.push({ id: idValue, connected: false });
      currentIds.add(idValue);
    });

    if (!obj.stepLinkData.length) return undefined;

    // Explicitly set connected=true for any non-disconnected links.
    obj.stepLinkData.forEach((e) => {
      if (!e) return;
      if (e.connected !== false) e.connected = true;
    });

    // Ensure connected items remain first (backend's getFirst() uses the first element)
    const connected = obj.stepLinkData.filter(
      (e) => e && e.connected !== false
    );
    const disconnected = obj.stepLinkData.filter(
      (e) => e && e.connected === false
    );
    obj.stepLinkData = [...connected, ...disconnected];

    return obj;
  };

  const pruneDisconnectedLinks = (slot) => {
    if (!slot || slot === null) return slot;
    const list = slot && slot.stepLinkData;
    if (!Array.isArray(list)) return slot;

    const connected = list.filter((e) => e && e.connected !== false);
    if (!connected.length) return undefined;

    connected.forEach((e) => {
      if (!e) return;
      e.connected = true;
    });
    slot.stepLinkData = connected;
    return slot;
  };

  allSteps.forEach((s) => {
    if (!s) return;
    s.source = undefined;
    s.target = undefined;
  });

  const isLoopBackFlowLine = (edge) => {
    const sourceHandle = String(edge?.sourceHandle ?? "");
    const targetHandle = String(edge?.targetHandle ?? "");

    return (
      sourceHandle.includes("__handle-top") &&
      targetHandle.includes("__handle-top")
    );
  };

  const outgoingFlowLines = new Map();
  activeFlowLines.forEach((edge) => {
    if (!edge || isLoopBackFlowLine(edge)) return;
    const src = edge.source;
    const tgt = edge.target;
    if (!src || !tgt) return;
    if (src !== "start" && !validIds.has(src)) return;
    if (!validIds.has(tgt)) return;

    const list = outgoingFlowLines.get(src) || [];
    list.push(edge);
    outgoingFlowLines.set(src, list);
  });

  const collectNextUnskippedTargets = (startId) => {
    const targets = new Set();
    const queue = [startId];
    const visited = new Set();

    while (queue.length) {
      const current = queue.shift();
      if (current && !visited.has(current)) {
        visited.add(current);

        const nextEdges = outgoingFlowLines.get(current) || [];
        nextEdges.forEach((edge) => {
          const nextId = edge?.target;
          if (!nextId || !validIds.has(nextId)) return;

          if (isSkippedId(nextId)) {
            queue.push(nextId);
          } else {
            targets.add(nextId);
          }
        });
      }
    }

    return Array.from(targets);
  };

  activeFlowLines.forEach((edge) => {
    if (!edge) return;
    const src = edge.source;
    const tgt = edge.target;
    if (!src || !tgt) return;

    const tgtStep = byId.get(tgt);
    if (src === "start") {
      if (isSkippedId(tgt)) {
        collectNextUnskippedTargets(tgt).forEach((nextTarget) => {
          const nextTargetStep = byId.get(nextTarget);
          if (nextTargetStep) nextTargetStep.source = null;
          startTargets.add(nextTarget);
        });
      } else {
        if (tgtStep) tgtStep.source = null; // start -> X
        startTargets.add(tgt);
      }
      return;
    }

    if (!validIds.has(src) || !validIds.has(tgt)) {
      // Skip edges referencing unknown ids
      return;
    }

    const srcStep = byId.get(src);
    const srcSkipped = isSkippedId(src);
    const tgtSkipped = isSkippedId(tgt);

    if (tgtStep && srcStep) {
      tgtStep.source =
        srcSkipped || tgtSkipped
          ? addSkippedLink(tgtStep.source, src)
          : addLink(tgtStep.source, src);
    }
    if (srcStep && tgt) {
      srcStep.target =
        srcSkipped || tgtSkipped
          ? addSkippedLink(srcStep.target, tgt)
          : addLink(srcStep.target, tgt);
    }

    if (!srcSkipped && tgtSkipped && srcStep) {
      collectNextUnskippedTargets(tgt).forEach((nextTarget) => {
        const nextTargetStep = byId.get(nextTarget);
        if (!nextTargetStep) return;

        srcStep.target = addLink(srcStep.target, nextTarget);
        nextTargetStep.source = addLink(nextTargetStep.source, src);
      });
    }
  });

  const outgoing = new Map();
  allSteps.forEach((step) => {
    const src = step?.origId;
    const targetLinks = Array.isArray(step?.target?.stepLinkData)
      ? step.target.stepLinkData
      : [];

    targetLinks.forEach((link) => {
      if (!src || !link?.id || link.connected === false) return;
      if (!validIds.has(String(src)) || !validIds.has(String(link.id))) return;

      const set = outgoing.get(String(src)) || new Set();
      set.add(String(link.id));
      outgoing.set(String(src), set);
    });
  });

  const reachableFromStart = new Set();
  const queue = Array.from(startTargets).filter((id) => validIds.has(id));
  while (queue.length) {
    const id = queue.shift();
    if (id && !reachableFromStart.has(id)) {
      reachableFromStart.add(id);

      const next = outgoing.get(id);
      if (next) {
        next.forEach((n) => {
          if (n && !reachableFromStart.has(n)) queue.push(n);
        });
      }
    }
  }

  // Re-attach preserved ids as disconnected links.
  allSteps.forEach((s) => {
    if (!s || !s.origId) return;
    const prev = previousLinks.get(s.origId) || {
      sourceIds: [],
      targetIds: [],
    };

    if (s.source !== null && !startTargets.has(s.origId)) {
      s.source = mergeDisconnected(s.source, prev.sourceIds);
    }
    s.target = mergeDisconnected(s.target, prev.targetIds);

    // Only prune disconnected links for steps that are connected to the flow start.
    // Detached subgraphs should keep their `connected:false` history.
    if (reachableFromStart.has(String(s.origId))) {
      s.source = pruneDisconnectedLinks(s.source);
      s.target = pruneDisconnectedLinks(s.target);
    }
  });

  allSteps.forEach((s) => {
    if (!s || !s.origId) return;

    const origId = String(s.origId);
    const hasSourceLinks =
      Array.isArray(s.source?.stepLinkData) && s.source.stepLinkData.length > 0;

    if (startTargets.has(origId)) return;
    if (reachableFromStart.has(origId)) return;
    if (s.source === null || hasSourceLinks) return;

    // Keep detached head steps out of the backend's first-step selection by
    // serializing a disconnected source marker when the subgraph has no path
    // from start and no preserved incoming link history.
    s.source = {
      stepLinkData: [{ id: origId, connected: false }],
    };
  });

  allSteps.forEach((s) => {
    const stepType = s && s.type ? String(s.type).toLowerCase() : "";
    if (stepType !== "router") return;

    s.target = null;

    if (!Array.isArray(s.routes)) return;

    s.routes.forEach((route) => {
      if (!route) return;

      const routerOrigId = s.origId ? String(s.origId) : "";
      const isRouteEntry = (rs) =>
        rs &&
        rs.origId &&
        String(rs.type).toUpperCase() !== "LOOP_END" &&
        Array.isArray(rs.source?.stepLinkData) &&
        rs.source.stepLinkData.some(
          (e) => e && String(e.id) === routerOrigId && e.connected !== false
        );

      const routeSteps = Array.isArray(route.steps) ? route.steps : [];
      const routeEntryStep = routeSteps.find(isRouteEntry);
      const firstStep =
        routeEntryStep && !isStepSkipped(routeEntryStep)
          ? routeEntryStep
          : findFirstUnskippedStep(routeSteps);
      const fallbackFirstStep =
        routeEntryStep || routeSteps.find((rs) => rs && rs.origId);
      const routeTargetStep = firstStep || fallbackFirstStep;
      const routeTargetConnected = Boolean(
        firstStep && !isStepSkipped(route) && !isStepSkipped(s)
      );

      route.source =
        s.origId && validIds.has(String(s.origId))
          ? {
              stepLinkData: [
                {
                  id: String(s.origId),
                  connected: routeTargetConnected,
                },
              ],
            }
          : undefined;

      route.target =
        routeTargetStep &&
        routeTargetStep.origId &&
        validIds.has(String(routeTargetStep.origId))
          ? {
              stepLinkData: [
                {
                  id: String(routeTargetStep.origId),
                  connected: routeTargetConnected,
                },
              ],
            }
          : undefined;

      if (
        routeTargetStep &&
        route.origId &&
        validIds.has(String(route.origId))
      ) {
        routeTargetStep.source = {
          stepLinkData: [
            { id: String(route.origId), connected: routeTargetConnected },
          ],
        };
      }
    });
  });

  return flow;
}

export async function checkAIReportExists(
  flowId: string,
  version?: number
): Promise<{
  exists: boolean;
  reportFlowVersion: number | null;
  pdfReady: boolean;
}> {
  const queryParams = version ? `?version=${version}` : "";
  const endpoint = `/reports/${flowId}/exists${queryParams}`;

  try {
    const response = await apiCall("GET", endpoint);

    if (response.code !== 200) {
      console.warn(`Report check API returned status: ${response.code}`);
      return { exists: false, reportFlowVersion: null, pdfReady: false };
    }

    return {
      exists: response.result?.exists === true,
      reportFlowVersion: response.result?.report_flow_version ?? null,
      pdfReady: response.result?.pdf_ready === true,
    };
  } catch (error) {
    console.warn("Report check failed:", error);
    return { exists: false, reportFlowVersion: null, pdfReady: false };
  }
}

export async function updateAIReport(
  flowId: string,
  flowData: any
): Promise<any> {
  const defaultModelId = await getProperty("ai.flow.report.model");
  const modelId = defaultModelId || "gemini-2.5-flash";
  const defaultTimeout = await getProperty("ai.flow.report.timeout");
  const timeout = Number(defaultTimeout) || 240;

  const endpoint = `/reports/update/${flowId}?model_id=${encodeURIComponent(
    modelId
  )}&timeout=${timeout}&use_websocket=true`;

  try {
    const response = await apiCall("PUT", endpoint, flowData);

    if (response.code === 200 || response.code === 202) {
      return response.result;
    }

    console.error("AI API Update Error, Status:", response.code);
    return { apiError: response.code };
  } catch (error) {
    console.error("AI API Update Error:", error);
    return { apiError: 0 };
  }
}

export async function generateAIReport(flowData: any): Promise<any> {
  const defaultModelId = await getProperty("ai.flow.report.model");
  const modelId = defaultModelId || "gemini-2.5-flash";
  const defaultTimeout = await getProperty("ai.flow.report.timeout");
  const timeout = Number(defaultTimeout) || 300;

  const endpoint = `/reports/generate?model_id=${encodeURIComponent(
    modelId
  )}&timeout=${timeout}&use_websocket=true`;

  try {
    const response = await apiCall("POST", endpoint, flowData);

    if (
      response.code === 200 ||
      response.code === 201 ||
      response.code === 202
    ) {
      return response.result;
    }

    console.error("AI API Error, Status:", response.code);
    return { apiError: response.code };
  } catch (error) {
    console.error("AI API Error:", error);
    return { apiError: 0 };
  }
}

export async function getAIReport(
  flowId: string,
  format: string,
  version?: number
): Promise<any> {
  const params: Record<string, any> = { format };
  if (version) {
    params.version = version;
  }

  const endpoint = `/reports/${flowId}/download`;

  try {
    const response = await apiCallParams(
      "GET",
      endpoint,
      params,
      REQUEST_TYPE.CONNECTOR,
      undefined,
      "blob"
    );

    if (response.code === 200 && response.result) {
      const blob = response.result as Blob;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const versionSuffix = version ? `_v${version}` : "";
      link.setAttribute(
        "download",
        `report-${flowId}${versionSuffix}.${format}`
      );

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    }

    console.error("Download failed, Status:", response.code);
    return false;
  } catch (error) {
    console.error("Report download error:", error);
    return false;
  }
}

export async function getAIReportHistory(flowId: string): Promise<any[]> {
  const endpoint = `/reports/${flowId}/history`;

  try {
    const response = await apiCall("GET", endpoint);

    if (response.code === 200) {
      return response.result || [];
    }

    console.warn("AI history fetch failed, Status:", response.code);
    return [];
  } catch (error) {
    console.warn("AI history fetch error:", error);
    return [];
  }
}

export interface JoltTransformationRequest {
  inputData: any;
  outputData?: any;
  context?: string;
}

export interface JoltTransformationResponse {
  transformationId?: string;
  status?: string;
  error?: string;
  timeoutSeconds?: number;
}

export async function getJoltTransformationResult(
  transformationId: string
): Promise<{ status: string; joltSpec?: any; errorMessage?: string } | null> {
  try {
    const response = await apiCall(
      "GET",
      `/jolt/transformations/${transformationId}/result`
    );
    if (response.code === 200 && response.result) {
      const data = response.result;
      return {
        status: data.status,
        joltSpec: data.jolt_specification,
        errorMessage: data.error_message,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export async function generateJoltTransformation(
  request: JoltTransformationRequest
): Promise<JoltTransformationResponse> {
  try {
    const [defaultJoltModelId, intentCheckModel, timeoutSecondsRaw] =
      await Promise.all([
        getProperty("ai.jolt.model"),
        getProperty("ai.jolt.intent-check-model"),
        getProperty("ai.jolt.timeout"),
      ]);
    const timeoutSeconds = timeoutSecondsRaw
      ? parseInt(timeoutSecondsRaw, 10)
      : 140;
    const requestBody: Record<string, any> = {
      input_data: request.inputData,
      model_id: defaultJoltModelId || "gemini-2.5-flash",
      intent_check_model: intentCheckModel || undefined,
      timeout_seconds: timeoutSeconds,
    };

    const hasOutputData =
      request.outputData !== undefined &&
      request.outputData !== null &&
      request.outputData !== "" &&
      !(Array.isArray(request.outputData) && request.outputData.length === 0) &&
      !(
        typeof request.outputData === "object" &&
        Object.keys(request.outputData).length === 0
      );

    if (hasOutputData) {
      requestBody.output_data = request.outputData;
    }

    if (
      request.context &&
      typeof request.context === "string" &&
      request.context.trim()
    ) {
      requestBody.context = request.context;
    }

    const endpoint = "/jolt/transformations";
    const response = await apiCall("POST", endpoint, requestBody);

    if (response.code === 202) {
      const data = response.result;
      const transformationId = data?.transformation_id || data?.id;

      if (!transformationId) {
        return {
          error: t("Flow.NoTransformationIdReturned"),
          status: "error",
        };
      }

      return {
        transformationId,
        status: "processing",
        timeoutSeconds,
      };
    }

    if (response.code === 401) {
      return { error: t("Flow.SessionExpired"), status: "error" };
    }
    if (response.code === 403) {
      return { error: t("Flow.PermissionDenied"), status: "error" };
    }
    if (response.code === 422) {
      return {
        error:
          t("Flow.JoltInvalidRequestData") ||
          "Invalid request data. Please check your Input and Output fields.",
        status: "error",
      };
    }
    if (response.code === 500) {
      return { error: t("Flow.ServerError"), status: "error" };
    }

    console.error("JOLT Transformer API Error, Status:", response.code);
    return {
      error: t("Flow.JoltApiError", { status: response.code }),
      status: "error",
    };
  } catch (error: any) {
    console.error("JOLT Transformer Error:", error);
    return {
      error: error.message || "Unknown error",
      status: "error",
    };
  }
}
